import React from 'react';
import type { CategoryScore, BackgroundResponse } from '../types/survey-student';

interface SimpleSurveyDisplayProps {
  scores: CategoryScore[];
  backgroundData: BackgroundResponse;
}

// Ring Chart Component
interface RingChartProps {
  score: number;
  maxScore: number;
  color: string;
  size?: number;
}

const RingChart: React.FC<RingChartProps> = ({ score, maxScore, color, size = 120 }) => {
  const percentage = (score / maxScore) * 100;
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="10"
          fill="transparent"
          strokeLinecap="round"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="10"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      {/* Score text in center */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-gray-900">{score.toFixed(1)}</span>
        <span className="text-xs text-gray-500">/ {maxScore}</span>
      </div>
    </div>
  );
};

// Color palette for different categories
const categoryColors: Record<string, string> = {
  'Intrinsic Goal Orientation': '#3b82f6', // Blue
  'Extrinsic Goal Motivation': '#8b5cf6', // Purple
  'Task Value': '#06b6d4', // Cyan
  'Control of Learning Beliefs': '#10b981', // Emerald
  'Self-Efficacy for Learning and Performance': '#f59e0b', // Amber
  'Test Anxiety': '#ef4444', // Red
  'Rehearsal': '#84cc16', // Lime
  'Elaboration': '#22c55e', // Green
  'Organization': '#14b8a6', // Teal
  'Critical Thinking': '#6366f1', // Indigo
  'Metacognitive Self-Regulation': '#a855f7', // Violet
  'Time and Study Environment': '#ec4899', // Pink
  'Effort Regulation': '#f97316', // Orange
  'Peer Learning': '#06b6d4', // Cyan
  'Help Seeking': '#8b5cf5' // Purple
};

const getScoreInterpretation = (score: number) => {
  if (score <= 3) return { level: 'Low', color: 'text-red-600', bgColor: 'bg-red-50' };
  if (score <= 5) return { level: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
  return { level: 'High', color: 'text-green-600', bgColor: 'bg-green-50' };
};

// Category groups to organize learning characteristics
interface CategoryGroup {
  name: string;
  description: string;
  categories: string[];
}

const categoryGroups: CategoryGroup[] = [
  {
    name: 'Value Component',
    description: 'How you value and engage with course material',
    categories: ['Intrinsic Goal Orientation', 'Extrinsic Goal Motivation', 'Task Value']
  },
  {
    name: 'Expectancy Component',
    description: 'Your beliefs about learning ability and success',
    categories: ['Control of Learning Beliefs', 'Self-Efficacy for Learning and Performance']
  },
  {
    name: 'Affective Component',
    description: 'Emotional aspects of learning',
    categories: ['Test Anxiety']
  },
  {
    name: 'Cognitive and Metacognitive Strategies',
    description: 'How you process and regulate learning',
    categories: ['Rehearsal', 'Organization', 'Elaboration', 'Critical Thinking', 'Metacognitive Self-Regulation']
  },
  {
    name: 'Resource Management Strategies',
    description: 'How you manage learning resources and environment',
    categories: ['Time and Study Environment', 'Effort Regulation', 'Peer Learning', 'Help Seeking']
  }
];

export const SimpleSurveyDisplay: React.FC<SimpleSurveyDisplayProps> = ({ scores, backgroundData }) => {
  const averageScore = scores.reduce((acc, s) => acc + s.score, 0) / scores.length;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Learning Profile Analysis</h2>
        <p className="text-gray-600">
          Comprehensive analysis of {scores.length} learning characteristics on a scale of 1-7
        </p>
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Low (1-3)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span>Medium (3-5)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>High (5-7)</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-emerald-50 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-emerald-800">Categories Analyzed</h3>
          <p className="text-2xl font-bold text-emerald-900">{scores.length}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-blue-800">Highest Score</h3>
          <p className="text-2xl font-bold text-blue-900">
            {Math.max(...scores.map(s => s.score)).toFixed(1)}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-purple-800">Average Score</h3>
          <p className="text-2xl font-bold text-purple-900">{averageScore.toFixed(1)}</p>
        </div>
      </div>

      {/* Learning Profile by Category Groups */}
      <div className="space-y-12">
        {categoryGroups.map((group) => (
          <div key={group.name} className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-xl font-semibold text-gray-900">{group.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{group.description}</p>
            </div>
            
            <div className="grid gap-6">
              {scores
                .filter(score => group.categories.includes(score.name))
                .map((category) => {
                  const interpretation = getScoreInterpretation(category.score);
                  const color = categoryColors[category.name] || '#6b7280';
                  
                  return (
                    <div 
                      key={category.name} 
                      className="bg-white rounded-lg shadow-md border border-gray-100 p-6"
                    >
                      <div className="flex items-start gap-6">
                        {/* Ring Chart */}
                        <div className="flex-shrink-0">
                          <RingChart
                            score={category.score}
                            maxScore={category.maxPossible}
                            color={color}
                            size={100}
                          />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">
                                {category.name}
                              </h4>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${interpretation.bgColor} ${interpretation.color}`}>
                                {interpretation.level}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">{category.score.toFixed(1)}</div>
                              <div className="text-sm text-gray-500">out of {category.maxPossible}</div>
                            </div>
                          </div>
                          
                          <p className="text-sm text-gray-600">{category.description}</p>
                          
                          {/* Show items/questions if available */}
                          {category.items && category.items.length > 0 && (
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Questions analyzed: </span>
                              {category.items.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {/* Background Information */}
      {backgroundData && Object.keys(backgroundData).length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Student Background</h3>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {backgroundData.name && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Name:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.name}</span>
                </div>
              )}
              {backgroundData.classLevel && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Class Level:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.classLevel}</span>
                </div>
              )}
              {backgroundData.major && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Major:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.major}</span>
                </div>
              )}
              {backgroundData.hometown && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Hometown:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.hometown}</span>
                </div>
              )}
              {backgroundData.firstLanguage && (
                <div>
                  <span className="text-sm font-medium text-gray-500">First Language:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.firstLanguage}</span>
                </div>
              )}
              {backgroundData.englishProficiency && (
                <div>
                  <span className="text-sm font-medium text-gray-500">English Proficiency:</span>
                  <span className="ml-2 text-sm text-gray-900">{backgroundData.englishProficiency}/10</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};